/**
 * Route pattern matching with the same rules as Lab's modal pop-up route targeting
 * (svc-lab `header-banners/path-pattern.ts`), so patterns can be copied between them:
 * - matched against the region-stripped path, e.g. `/checkout/123` not `/au/checkout/123`
 * - `/` matches the home page only
 * - `:param` matches a single segment
 * - a trailing `/*` matches the path itself and anything below it
 * Invalid patterns never match.
 */
const PARAM_SEGMENT_PATTERN = /^:[A-Za-z0-9_-]+$/

function escapeRegExpSegment(segment: string): string {
  return segment.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&')
}

function compileRoutePattern(rawPattern: string): RegExp | undefined {
  const pattern = rawPattern.trim()
  if (!pattern.startsWith('/') || pattern.startsWith('//')) return undefined

  const hasWildcardSuffix = pattern.endsWith('/*')
  if (pattern.includes('*') && !hasWildcardSuffix) return undefined

  const basePath = hasWildcardSuffix ? pattern.slice(0, -2) : pattern
  const baseWithoutLeadingSlash = basePath.slice(1)
  if (baseWithoutLeadingSlash.length === 0) {
    return hasWildcardSuffix ? /^\/(?:.*)?$/ : /^\/$/
  }

  const segments = baseWithoutLeadingSlash.split('/')
  if (segments.some(segment => segment.length === 0)) return undefined

  const regexSegments: Array<string> = []
  for (const segment of segments) {
    if (segment.startsWith(':')) {
      if (!PARAM_SEGMENT_PATTERN.test(segment)) return undefined
      regexSegments.push('[^/]+')
    } else {
      regexSegments.push(escapeRegExpSegment(segment))
    }
  }

  return new RegExp(`^/${regexSegments.join('/')}${hasWildcardSuffix ? '(?:/.*)?' : ''}$`)
}

// Patterns come from a handful of Lab payloads, so the cache stays tiny
const compiledPatterns = new Map<string, RegExp | undefined>()

function getCompiledRoutePattern(pattern: string): RegExp | undefined {
  if (!compiledPatterns.has(pattern)) {
    compiledPatterns.set(pattern, compileRoutePattern(pattern))
  }
  return compiledPatterns.get(pattern)
}

export function isPathMatchedByPatterns(path: string, patterns: ReadonlyArray<string>): boolean {
  return patterns.some(pattern => getCompiledRoutePattern(pattern)?.test(path) ?? false)
}
